import { getData, getName } from "country-list";
import {
  beautifyDurationUnit,
  calculateFinancialSupport,
  toCurrency,
} from "../../../helpers";
import { beautifyRequiredDocuments } from "./helperFunctions";
import {
  IAccommodationFee,
  IFee,
  ILanguageCourseViewMoreBackEndModel,
  ILanguageCourseViewMoreFrontEndModel,
} from "../types";
import {
  COURSE_TYPES,
  courseDetailsConfig,
  LANGUAGES,
} from "../../language-course/constants";
import {
  calcFeeWithDiscount,
  sumPriceAndOtherFeesSupports,
} from "../../language-course/helpers/helperFunctions";
import { LanguageCourseAccommodationFee } from "../../language-course/types";

export const parseToLanguageCourseViewMoreFrontEndModel = (
  course: ILanguageCourseViewMoreBackEndModel,
) => {
  const schoolImage = course.school.image?.preview;
  const schoolCountry = getName(course.school.country)!;
  const requiredDocs = beautifyRequiredDocuments(course.requiredDocs);
  const courseType = COURSE_TYPES.find(
    (type) => type.id === course.courseType,
  )!.name;
  const branches = course.branches.map((branch) => branch.name);
  const countries = getData();
  const restrictedCountries = course.restrictedCountries?.map(
    (country) => countries.find((c) => c.code === country)!.name,
  );

  const programDuration = `${course.durationAmount} ${beautifyDurationUnit(
    course.durationAmount,
    course.durationUnit,
    "Course",
  )}`;

  const priceDuration = `${
    course.priceDurationAmount == null ? "" : course.priceDurationAmount + " "
  }
        ${beautifyDurationUnit(
          course.priceDurationAmount,
          course.priceDurationUnit,
          "Course",
        )}`;

  const details = course.details
    .sort((a, b) => a.type - b.type)
    .map((detail) => ({
      name: courseDetailsConfig.find((d) => d.type === detail.type)!.name,
      content: detail.content,
      type: detail.type,
    }));

  const {
    feeWithCurrency: priceWithCurrency,
    calcFee: calcPrice,
    calcFeeWithCurrency: calcPriceWithCurrency,
    discountWithCurrency: priceDiscountWithCurrency,
    hasSupport: hasPriceSupport,
  } = calcFeeWithDiscount({
    fee: course.price,
    hasFeeDiscount: course.hasPriceDiscount,
    feeDiscountType: course.priceDiscountType,
    feeDiscountAmount: course.priceDiscountAmount,
    currency: course.currency,
  });

  const {
    feeWithCurrency: otherFeesWithCurrency,
    calcFee: calcOtherFees,
    calcFeeWithCurrency: calcOtherFeesWithCurrency,
    discountWithCurrency: otherFeesDiscountWithCurrency,
    hasSupport: hasOtherFeesSupport,
  } = calcFeeWithDiscount({
    fee: course.otherFeesTotal,
    hasFeeDiscount: course.hasOtherFeesDiscount,
    feeDiscountType: course.otherFeesDiscountType,
    feeDiscountAmount: course.otherFeesDiscountAmount,
    currency: course.currency,
  });

  const { priceOtherFeesSum, priceOtherFeesWithSupportsSum, hasFeesSupport } =
    sumPriceAndOtherFeesSupports(
      course.price,
      hasPriceSupport,
      calcPrice,
      course.otherFeesTotal,
      hasOtherFeesSupport,
      calcOtherFees,
      course.currency,
    );

  const commission = calculateFinancialSupport(
    course.price,
    true,
    course.commissionType,
    course.commissionAmount,
  );

  const otherFees = course.otherFees.map((fee) => ({
    name: fee.name,
    amount: toCurrency(fee.amount, course.currency, " ", "code"),
  }));

  const accommodationFees: IAccommodationFee[] = [];

  course.residenceAmount !== null &&
    accommodationFees.push({
      name: LanguageCourseAccommodationFee.RESIDENCE,
      label: "Residence",
      amount: course.residenceAmount!,
      amountWithCurrency: toCurrency(
        course.residenceAmount,
        course.currency,
        " ",
        "code",
      ),
    });

  course.homestayAmount !== null &&
    accommodationFees.push({
      name: LanguageCourseAccommodationFee.HOMESTAY,
      label: "Homestay",
      amount: course.homestayAmount!,
      amountWithCurrency: toCurrency(
        course.homestayAmount,
        course.currency,
        " ",
        "code",
      ),
    });

  course.insuranceAmount !== null &&
    accommodationFees.push({
      name: LanguageCourseAccommodationFee.INSURANCE,
      label: "Insurance",
      amount: course.insuranceAmount!,
      amountWithCurrency: toCurrency(
        course.insuranceAmount,
        course.currency,
        " ",
        "code",
      ),
    });

  const discounts: IFee[] = [
    {
      name: "Price",
      amount: priceDiscountWithCurrency,
    },
    {
      name: "Other Fees",
      amount: otherFeesDiscountWithCurrency,
    },
  ];

  const parsedProgram: ILanguageCourseViewMoreFrontEndModel = {
    language: LANGUAGES.find((l) => l.id === course.language)!.name,
    category: course.category,
    restrictedCountries,
    requiredDocs,
    courseType,
    branches,
    details,
    school: {
      website: course.school.website,
      typeId: course.school.typeId,
      phone: course.school.phone,
      email: course.school.email,
      name: course.school.name,
      country: schoolCountry,
      image: schoolImage,
    },
    fees: {
      supports: [
        {
          name: "Discount",
          support: discounts,
        },
      ],
      commission: toCurrency(commission, course.currency, " ", "code"),
      accommodationFees: {
        fees: accommodationFees,
        hasFees: true,
        zeroFee: toCurrency(0, course.currency, " ", "code"),
      },
      otherFees: {
        otherFeesTotalWithSupport: calcOtherFeesWithCurrency,
        otherFeesTotal: otherFeesWithCurrency,
        hasSupport: hasOtherFeesSupport,
        fees: otherFees,
      },
      total: {
        totalWithSupport: priceOtherFeesWithSupportsSum,
        total: priceOtherFeesSum,
        hasSupport: hasFeesSupport,
      },
      mainFee: {
        name: "Price",
        feeWithSupport: calcPriceWithCurrency,
        hasSupport: hasPriceSupport,
        fee: priceWithCurrency,
      },
      currencyId: course.currency,
      duration: priceDuration,
    },
    housingTypeNames: course.housingTypeNames.join(", "),
    extraDocuments: course.detailFiles,
    duration: programDuration,
    isMasked: course.isMasked,
  };

  return parsedProgram;
};
